import {ref, computed, watch} from 'vue'
import {defineStore} from 'pinia'
import type {Material, TranslatedMaterial} from '@shared/types'
import {useLocalStore} from './local'
import {clientApi, cpApi, type TranslationStatus} from '@/api'
import {notifications as message} from '@/lib/notifications'
const notification = message

export const useTranslationsStore = defineStore('translations', () => {
  // State
  const targetLang = ref<string>('')
  const nativeLang = ref<string>('')
  const level = ref<number>(1)
  const unit = ref<number>(1)
  const lesson = ref<number>(1)
  const materialId = ref<number | null>(null)

  const levels = ref<{level: number; slides_count: number}[]>([])
  const units = ref<{level: number; unit: number}[]>([])
  const lessons = ref<{level: number; unit: number; lesson: number; slides_count: number}[]>([])
  const slides = ref<
    {level: number; unit: number; lesson: number; slide: number; title: string; material_id: number}[]
  >([])
  const translationStatuses = ref<{translate_id: number; material_id: number; status: 'check' | 'ok' | 'sample'}[]>([])

  const material = ref<Material | null>(null)
  const translation = ref<TranslatedMaterial | null>(null)
  const editedTranslation = ref<{
    title: string
    data: {default: string; commented: string; error?: string | null}[]
  }>({
    title: '',
    data: [],
  })
  const editedTranslationWithoutErrors = computed(() => {
    return {
      title: editedTranslation.value.title,
      data: editedTranslation.value.data.map((item) => ({
        default: item.default,
        commented: item.commented,
      })),
    }
  })

  const isGeneratingTranslation = ref<boolean>(false)
  const isBulkGenerating = ref<boolean>(false)
  const bulkGenerationProgress = ref<number>(0)
  const totalSlidesToGenerate = ref<number>(0)

  // Computed properties
  const filteredUnits = computed(() => units.value.filter((u) => u.level === level.value))
  const filteredLessons = computed(() => lessons.value.filter((l) => l.level === level.value && l.unit === unit.value))
  const availableTargetLangs = ['en', 'de', 'it', 'pl']
  const availableNativeLangs = computed(() =>
    ['en', 'ru', 'de', 'it', 'pl'].filter((lang) => lang !== targetLang.value)
  )
  const showMaterialSelectors = computed(() => targetLang.value && nativeLang.value)

  // Initialize from local store
  const initFromLocalStore = () => {
    const localStore = useLocalStore()
    targetLang.value = localStore.lastTargetLang
    nativeLang.value = localStore.lastNativeLang
    level.value = localStore.lastLevel
    unit.value = localStore.lastUnit
    lesson.value = localStore.lastLesson
    materialId.value = localStore.lastMaterialId

    // Set up watchers to save selector values
    watch(targetLang, (newValue) => {
      localStore.lastTargetLang = newValue
    })

    watch(nativeLang, (newValue) => {
      localStore.lastNativeLang = newValue
    })

    watch(level, (newValue) => {
      localStore.lastLevel = newValue
    })

    watch(unit, (newValue) => {
      localStore.lastUnit = newValue
    })

    watch(lesson, (newValue) => {
      localStore.lastLesson = newValue
    })

    watch(materialId, (newValue) => {
      localStore.lastMaterialId = newValue
    })
  }

  // Methods
  const updateMaterialIndex = async () => {
    if (targetLang.value && nativeLang.value) {
      const [indexResponse, statusesResponse] = await Promise.all([
        clientApi.materials.getMaterialIndex(targetLang.value),
        cpApi.translation.getTranslations<TranslationStatus>({
          lang: nativeLang.value,
          fields: 'translate_id,material_id,status',
          target_lang: targetLang.value,
        }),
      ])

      levels.value = indexResponse.levels
      units.value = indexResponse.units
      lessons.value = indexResponse.lessons
      translationStatuses.value = statusesResponse

      // Reset selections if they are not in the index
      if (!levels.value.find((l) => l.level === level.value)) {
        level.value = 1
      }
      if (!filteredUnits.value.find((u) => u.unit === unit.value)) {
        unit.value = 1
      }
      if (!filteredLessons.value.find((l) => l.lesson === lesson.value)) {
        lesson.value = 1
      }
      if (slides.value.length > 0 && !slides.value.find((s) => s.material_id === materialId.value)) {
        materialId.value = slides.value[0].material_id
      }
    }
  }

  const getSlides = async () => {
    if (level.value && unit.value && lesson.value) {
      const response = await clientApi.materials.getSlides(
        targetLang.value,
        nativeLang.value,
        level.value,
        unit.value,
        lesson.value
      )
      slides.value = response
      // multisort by level, unit, lesson, slide
      slides.value.sort((a, b) => {
        if (a.level !== b.level) return a.level - b.level
        if (a.unit !== b.unit) return a.unit - b.unit
        if (a.lesson !== b.lesson) return a.lesson - b.lesson
        return a.slide - b.slide
      })
      if (slides.value.length > 0 && !slides.value.find((s) => s.material_id === materialId.value)) {
        materialId.value = slides.value[0].material_id
      }
    }
  }

  const getMaterial = async () => {
    if (level.value && unit.value && lesson.value && materialId.value) {
      const response = await clientApi.materials.getMaterialById(materialId.value)
      material.value = response
      await getTranslation()
    }
  }

  const getTranslation = async () => {
    if (material.value?.material_id) {
      try {
        const params = {
          material_id: material.value.material_id,
          lang: nativeLang.value,
        }

        const response = await cpApi.translation.getTranslations<TranslatedMaterial>(params)
        translation.value = response[0] || null // Take first result since we're filtering by material_id and lang

        // Update edited translation
        if (translation.value) {
          editedTranslation.value = {
            title: translation.value.title || '',
            data: translation.value.data.map((item) => ({
              default: item.default || '',
              commented: item.commented || '',
              error: item.error ?? undefined,
            })),
          }
        } else {
          editedTranslation.value = {
            title: '',
            data: [],
          }
        }
      } catch (error) {
        console.error('Failed to fetch translation:', error)
        message.error('Failed to fetch translation')
        translation.value = null
      }
    }
  }

  const getSlideStatus = (materialId: number) => {
    return translationStatuses.value.find((s) => s.material_id === materialId)?.status || null
  }

  const findNextSlideToCheck = async () => {
    // First try to find in current lesson
    const currentLessonSlides = slides.value.filter(
      (s) => s.level === level.value && s.unit === unit.value && s.lesson === lesson.value
    )

    const currentSlideIndex = currentLessonSlides.findIndex((s) => s.material_id === materialId.value)
    const nextSlideInLesson = currentLessonSlides
      .slice(currentSlideIndex + 1)
      .find((s: {material_id: number}) => getSlideStatus(s.material_id) === 'check')

    if (nextSlideInLesson) {
      materialId.value = nextSlideInLesson.material_id
      return true
    }

    // Try to find in next lessons of current unit
    const currentUnitLessons = lessons.value
      .filter((l) => l.level === level.value && l.unit === unit.value)
      .sort((a, b) => a.lesson - b.lesson)

    for (let i = 0; i < currentUnitLessons.length; i++) {
      const lessonData = currentUnitLessons[i]
      if (lessonData.lesson <= lesson.value) continue

      // Load slides for the next lesson
      const lessonSlides = await clientApi.materials.getSlides(
        targetLang.value,
        nativeLang.value,
        level.value,
        unit.value,
        lessonData.lesson
      )

      const slideToCheck = lessonSlides.find((s: {material_id: number}) => getSlideStatus(s.material_id) === 'check')
      if (slideToCheck) {
        lesson.value = lessonData.lesson
        slides.value = lessonSlides
        materialId.value = slideToCheck.material_id
        return true
      }
    }

    // Try to find in next units of current level
    const currentLevelUnits = units.value.filter((u) => u.level === level.value).sort((a, b) => a.unit - b.unit)

    for (let i = 0; i < currentLevelUnits.length; i++) {
      const unitData = currentLevelUnits[i]
      if (unitData.unit <= unit.value) continue

      const unitLessons = lessons.value
        .filter((l) => l.level === level.value && l.unit === unitData.unit)
        .sort((a, b) => a.lesson - b.lesson)

      for (const lessonData of unitLessons) {
        // Load slides for each lesson in the next unit
        const lessonSlides = await clientApi.materials.getSlides(
          targetLang.value,
          nativeLang.value,
          level.value,
          unitData.unit,
          lessonData.lesson
        )

        const slideToCheck = lessonSlides.find((s: {material_id: number}) => getSlideStatus(s.material_id) === 'check')
        if (slideToCheck) {
          unit.value = unitData.unit
          lesson.value = lessonData.lesson
          slides.value = lessonSlides
          materialId.value = slideToCheck.material_id
          return true
        }
      }
    }

    // Try to find in next levels
    const allLevels = levels.value.map((l) => l.level).sort((a, b) => a - b)
    const currentLevelIndex = allLevels.indexOf(level.value)

    for (let i = currentLevelIndex + 1; i < allLevels.length; i++) {
      const nextLevel = allLevels[i]
      const levelUnits = units.value.filter((u) => u.level === nextLevel).sort((a, b) => a.unit - b.unit)

      for (const unitData of levelUnits) {
        const unitLessons = lessons.value
          .filter((l) => l.level === nextLevel && l.unit === unitData.unit)
          .sort((a, b) => a.lesson - b.lesson)

        for (const lessonData of unitLessons) {
          // Load slides for each lesson in the next level
          const lessonSlides = await clientApi.materials.getSlides(
            targetLang.value,
            nativeLang.value,
            nextLevel,
            unitData.unit,
            lessonData.lesson
          )

          const slideToCheck = lessonSlides.find(
            (s: {material_id: number}) => getSlideStatus(s.material_id) === 'check'
          )
          if (slideToCheck) {
            level.value = nextLevel
            unit.value = unitData.unit
            lesson.value = lessonData.lesson
            slides.value = lessonSlides
            materialId.value = slideToCheck.material_id
            return true
          }
        }
      }
    }

    return false
  }

  const goToNextCheck = async () => {
    const found = await findNextSlideToCheck()
    if (!found) {
      // Show message that no more slides to check were found
      message.info('No more slides to check')
    }
  }

  // Translation generation methods
  const generateTranslation = async () => {
    if (!material.value?.material_id) return

    try {
      isGeneratingTranslation.value = true
      const response = await cpApi.translation.generate(material.value.material_id, nativeLang.value)
      if (response) {
        editedTranslation.value = {
          title: response.title || '',
          data: response.translations.map((item) => ({
            default: item.default || '',
            commented: item.commented || '',
            error: item.error || null,
          })),
        }
      }
    } catch (error) {
      console.error('Failed to generate translation:', error)
      notification.error({
        message: 'Failed to generate translation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      isGeneratingTranslation.value = false
    }
  }

  const generateCommentedTranslation = async () => {
    if (!material.value?.material_id) return

    try {
      isGeneratingTranslation.value = true
      // Prepare current translations for sending to the server
      const currentTranslations = editedTranslation.value.data.map((item) => ({
        default: item.default || '',
      }))

      const response = await cpApi.translation.generateCommented(material.value.material_id, {
        lang: nativeLang.value,
        translations: currentTranslations,
      })

      if (response) {
        // Update only commented translations, keeping current default translations
        editedTranslation.value.data = editedTranslation.value.data.map((item, index) => ({
          ...item,
          commented: response.translations[index]?.commented || '',
          error: response.translations[index]?.error || null,
        }))
      }
    } catch (error) {
      console.error('Failed to generate commented translation:', error)
      notification.error({
        message: 'Failed to generate commented translation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      isGeneratingTranslation.value = false
    }
  }

  const generateDefaultTranslation = async () => {
    if (!material.value?.material_id) return

    try {
      isGeneratingTranslation.value = true
      const response = await cpApi.translation.generateDefault(material.value.material_id, nativeLang.value)

      if (response) {
        // Update only default translations, clear commented translations
        editedTranslation.value.data = editedTranslation.value.data.map((item, index) => ({
          ...item,
          default: response.translations[index]?.default || item.default || '',
          commented: '', // Clear commented translations
          error: response.translations[index]?.error || null,
        }))
      }
    } catch (error) {
      console.error('Failed to generate default translation:', error)
      notification.error({
        message: 'Failed to generate default translation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      isGeneratingTranslation.value = false
    }
  }

  const generateCommentedTranslationForSentence = async (index: number) => {
    if (!material.value?.material_id) return

    try {
      isGeneratingTranslation.value = true
      // Prepare current translations for sending to the server
      const currentTranslations = editedTranslation.value.data.map((item) => ({
        default: item.default || '',
      }))

      const response = await cpApi.translation.generateCommented(material.value.material_id, {
        lang: nativeLang.value,
        translations: currentTranslations,
        index: index,
      })

      if (response) {
        // Update only the commented translation for the specific sentence
        editedTranslation.value.data[index] = {
          ...editedTranslation.value.data[index],
          commented: response.translations[index]?.commented || '',
          error: response.translations[index]?.error || null,
        }
      }
    } catch (error) {
      console.error('Failed to generate commented translation for sentence:', error)
      notification.error({
        message: 'Failed to generate commented translation for sentence',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      isGeneratingTranslation.value = false
    }
  }

  const startBulkGeneration = async () => {
    if (isBulkGenerating.value) return

    try {
      isBulkGenerating.value = true
      bulkGenerationProgress.value = 0

      // Get all slides for current lesson
      const allSlides = slides.value

      // Filter slides to only include those with status 'check' or no status
      const slidesToProcess = allSlides.filter((slideData) => {
        const status = getSlideStatus(slideData.material_id)
        return status === 'check' || status === null
      })

      totalSlidesToGenerate.value = slidesToProcess.length

      if (slidesToProcess.length === 0) {
        message.info('No slides with status "check" found to process')
        isBulkGenerating.value = false
        return
      }

      // For each slide, generate translation
      for (let i = 0; i < slidesToProcess.length; i++) {
        if (!isBulkGenerating.value) break // Check if generation was stopped

        const slideData = slidesToProcess[i]
        const slideToGenerate = slideData.slide

        // Get slide by lesson and slide number
        const currentMaterial = await clientApi.materials.getMaterialById(slideData.material_id)
        const currentTranslation = (
          await cpApi.translation.getTranslations<TranslatedMaterial>({
            material_id: currentMaterial.material_id,
            lang: nativeLang.value,
          })
        )?.[0]

        // Generate translation
        try {
          const response = await cpApi.translation.generate(currentMaterial.material_id, nativeLang.value)

          // Save translation
          if (response) {
            const data = response.translations.map((item) => ({
              default: item.default,
              commented: item.commented || null,
              error: item.error,
            }))
            if (currentTranslation) {
              await cpApi.translation.update(currentTranslation.translate_id, {
                title: response.title,
                status: 'check',
                data,
              })
            } else {
              await cpApi.translation.create({
                material_id: currentMaterial.material_id,
                lang: nativeLang.value,
                title: response.title,
                data,
                status: 'check',
              })
            }
          }
        } catch (error) {
          notification.error({
            message: `Failed to generate translation ${slideData.level}.${slideData.unit}.${slideData.lesson}.${slideData.slide}`,
            description: error instanceof Error ? error.message : 'Unknown error',
            duration: 0,
          })
          console.error(`Failed to generate translation for slide ${slideToGenerate}:`, error)
        }

        // Update progress
        bulkGenerationProgress.value = (100 * (i + 1)) / slidesToProcess.length
      }

      // Refresh current translation if it was generated
      if (material.value?.material_id) {
        await getTranslation()
      }

      message.success('Bulk generation completed')
    } catch (error) {
      console.error('Failed to start bulk generation:', error)
      notification.error({
        message: 'Failed to start bulk generation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    } finally {
      isBulkGenerating.value = false
      bulkGenerationProgress.value = 0
    }
  }

  const stopBulkGeneration = () => {
    isBulkGenerating.value = false
    message.info('Bulk generation stopped')
  }

  // Save and update methods
  const saveTranslation = async (status: 'check' | 'ok' | 'sample') => {
    if (!material.value) return

    try {
      let response
      if (translation.value?.translate_id) {
        // Update existing translation
        response = await cpApi.translation.update(translation.value.translate_id, {
          title: editedTranslationWithoutErrors.value.title,
          data: editedTranslationWithoutErrors.value.data,
          status,
        })

        // Update status in the list
        const statusIndex = translationStatuses.value.findIndex(
          (s) => s.translate_id === translation.value?.translate_id
        )
        if (statusIndex !== -1) {
          translationStatuses.value[statusIndex].status = status
        }

        // Update translation status
        if (translation.value) {
          translation.value.status = status
        }
      } else {
        // Create new translation
        response = await cpApi.translation.create({
          material_id: material.value.material_id,
          lang: nativeLang.value,
          title: editedTranslationWithoutErrors.value.title,
          data: editedTranslationWithoutErrors.value.data,
          status,
        })

        // Update status in the list if response contains translate_id
        if (response && response.translate_id) {
          const newStatus = {
            translate_id: response.translate_id,
            material_id: material.value.material_id,
            status,
          }

          const statusIndex = translationStatuses.value.findIndex((s) => s.material_id === material.value?.material_id)
          if (statusIndex !== -1) {
            translationStatuses.value[statusIndex] = newStatus
          } else {
            translationStatuses.value.push(newStatus)
          }
        }
      }

      // Refresh translation data
      await getTranslation()

      return response
    } catch (error) {
      console.error('Failed to save translation:', error)
      notification.error({
        message: 'Failed to save translation',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      return null
    }
  }

  const updateStatus = async (status: 'check' | 'ok' | 'sample') => {
    if (!translation.value) return

    try {
      await cpApi.translation.update(translation.value.translate_id, {status})

      // Update status in the list
      const statusIndex = translationStatuses.value.findIndex((s) => s.translate_id === translation.value?.translate_id)
      if (statusIndex !== -1) {
        translationStatuses.value[statusIndex].status = status
      }

      // Update translation status
      if (translation.value) {
        translation.value.status = status
      }

      return true
    } catch (error) {
      console.error('Failed to update translation status:', error)
      notification.error({
        message: 'Failed to update translation status',
        description: error instanceof Error ? error.message : 'Unknown error',
      })
      return false
    }
  }

  // Helper methods
  const getImageUrl = (image: string) => {
    return import.meta.env.VITE_BASE_URL + '/media/' + image.replace(/\.jpg$/g, '_m.jpg')
  }

  const postprocessCommentedTranslation = (commented: string) => {
    return commented.replace(/<span title="([^"]+)">([^<]+)<\/span>/g, (match, p1, p2) => {
      const titleLength = p1.trim().toLowerCase().length * 0.65
      const contentLength = p2.trim().toLowerCase().length
      let style = ''
      if (titleLength > contentLength) {
        const extraLength = titleLength - contentLength
        style = ` style="padding: 0 ${extraLength / 2}em"`
      }
      return `<span title="${p1}"${style}>${p2}</span>`
    })
  }

  const arabicToRomanNumber = (number: number) => {
    const romanNumerals = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X']
    return romanNumerals[number - 1]
  }

  // Reset all state
  const reset = () => {
    targetLang.value = ''
    nativeLang.value = ''
    level.value = 1
    unit.value = 1
    lesson.value = 1
    materialId.value = null

    levels.value = []
    units.value = []
    lessons.value = []
    slides.value = []
    translationStatuses.value = []

    material.value = null
    translation.value = null
    editedTranslation.value = {
      title: '',
      data: [],
    }

    isGeneratingTranslation.value = false
    isBulkGenerating.value = false
    bulkGenerationProgress.value = 0
    totalSlidesToGenerate.value = 0
  }

  return {
    // State
    targetLang,
    nativeLang,
    level,
    unit,
    lesson,
    materialId,
    levels,
    units,
    lessons,
    slides,
    translationStatuses,
    material,
    translation,
    editedTranslation,
    isGeneratingTranslation,
    isBulkGenerating,
    bulkGenerationProgress,
    totalSlidesToGenerate,

    // Computed
    filteredUnits,
    filteredLessons,
    availableTargetLangs,
    availableNativeLangs,
    showMaterialSelectors,

    // Methods
    initFromLocalStore,
    updateMaterialIndex,
    getSlides,
    getMaterial,
    getTranslation,
    getSlideStatus,
    findNextSlideToCheck,
    goToNextCheck,
    generateTranslation,
    generateCommentedTranslation,
    generateDefaultTranslation,
    generateCommentedTranslationForSentence,
    startBulkGeneration,
    stopBulkGeneration,
    saveTranslation,
    updateStatus,
    getImageUrl,
    postprocessCommentedTranslation,
    arabicToRomanNumber,
    reset,
  }
})
